import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { client } from '@/client'

const routes: RouteRecordRaw[] = [
  { path: '/', name: 'home', component: () => import('@/views/HomeView.vue') },
  {
    path: '/signin',
    name: 'signin',
    component: () => import('@/views/SignInView.vue'),
    meta: { bare: true },
  },
  {
    path: '/room/:slug',
    name: 'room',
    component: () => import('@/views/RoomView.vue'),
    meta: { bare: true },
    // Deliberately NOT auth-guarded. A meeting link must work for someone who
    // has never heard of the product — the room itself handles the guest pass.
  },
  {
    path: '/room/:slug/recordings',
    name: 'recordings',
    component: () => import('@/views/RecordingsView.vue'),
  },
  // One meeting's transcript (Phase 10), beside its recording.
  {
    path: '/room/:slug/meetings/:sessionId',
    name: 'meeting',
    component: () => import('@/views/MeetingView.vue'),
  },
  // What a room's AI features cost this month, for its hosts (Phase 10).
  {
    path: '/room/:slug/ai',
    name: 'room-ai',
    component: () => import('@/views/AiUsageView.vue'),
  },
  // Which AI service sees what: linked from the captions notice and the AI settings.
  { path: '/ai-data', name: 'ai-data', component: () => import('@/views/AiDataView.vue') },
  // Recording a video (Phase 9) needs an account; the page says so itself.
  { path: '/record', name: 'record', component: () => import('@/views/RecordView.vue') },
  { path: '/videos', name: 'videos', component: () => import('@/views/VideosView.vue') },
  // A video's link works for anyone who holds it — no account, like a meeting link.
  { path: '/v/:token', name: 'video', component: () => import('@/views/VideoView.vue') },
  // Editing a video: its own page, the whole window. The page sends anyone but its owner back.
  {
    path: '/v/:token/edit',
    name: 'video-editor',
    component: () => import('@/views/EditorView.vue'),
    meta: { bare: true },
  },
  // The company route: an organisation's people, teams and connected tools.
  {
    path: '/org',
    component: () => import('@/views/OrgView.vue'),
    children: [
      { path: '', name: 'org', component: () => import('@/views/org/OrgHome.vue') },
      { path: 'calendar', name: 'org-calendar', component: () => import('@/views/org/OrgCalendar.vue') },
      { path: 'people', name: 'org-people', component: () => import('@/views/org/OrgPeople.vue') },
      { path: 'teams', name: 'org-teams', component: () => import('@/views/org/OrgTeams.vue') },
      { path: 'connectors', name: 'org-connectors', component: () => import('@/views/org/OrgConnectors.vue') },
      // Each connector has its own page: its connection, its settings and what it has brought in.
      { path: 'connectors/jira', name: 'org-connector-jira', component: () => import('@/views/org/OrgJira.vue') },
      // The page was called Integrations before connectors was the word for them.
      { path: 'integrations', redirect: { name: 'org-connectors' } },
    ],
  },
  // An invite link to an organisation: open it signed in or out.
  { path: '/join/org/:token', name: 'join-org', component: () => import('@/views/JoinOrgView.vue') },
  // Admins: which AI model the video editor uses, and its key. The page says when it is not yours.
  { path: '/admin', name: 'admin', component: () => import('@/views/AdminView.vue') },
  { path: '/system', name: 'system-check', component: () => import('@/views/SystemCheckView.vue') },
  { path: '/:pathMatch(.*)*', redirect: '/' },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

let restored = false

router.beforeEach(async () => {
  // One attempt per page load to trade the refresh cookie for a session.
  // Without awaiting it, a reload lands on the signed-out view for a beat and
  // then flips, which reads as a bug.
  if (!restored) {
    restored = true
    await client.auth.restore()
  }
  return true
})
